/**
 * LegalMentionsStep - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * OPTIONNEL - Avec boutons de suggestions
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollText, ArrowRight, SkipForward, Lightbulb, Sparkles } from 'lucide-react';
import { companyAPI } from '@/utils/api';
import { logger } from '@/utils/logger';

interface LegalMentionsStepProps {
  onComplete: () => void;
  onSkip: () => void;
}

// Suggestions de mentions légales - keys resolved at render time via t()
const LEGAL_SUGGESTION_KEYS = [
  { titleKey: 'onboarding.legal.suggestions.microTitle', textKey: 'onboarding.legal.suggestions.microText' },
  { titleKey: 'onboarding.legal.suggestions.autoTitle', textKey: 'onboarding.legal.suggestions.autoText' },
  { titleKey: 'onboarding.legal.suggestions.standardTitle', textKey: 'onboarding.legal.suggestions.standardText' },
  { titleKey: 'onboarding.legal.suggestions.sarlTitle', textKey: 'onboarding.legal.suggestions.sarlText' },
];

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  isLoading?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : variant === 'ghost'
        ? 'text-white/60 hover:text-white hover:bg-white/10'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const LegalMentionsStep: React.FC<LegalMentionsStepProps> = ({ onComplete, onSkip }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    legalNotesDefault: '',
    defaultPaymentTermsDays: 30,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setIsLoading(true);

      const companies = await companyAPI.getAll();

      if (!companies || companies.length === 0) {
        throw new Error(t('onboarding.legal.noCompany'));
      }

      const company = companies[0];
      await companyAPI.update(company.id, formData);

      logger.log('✅ Mentions légales et conditions de paiement enregistrées');
      onComplete();
    } catch (err: any) {
      logger.error('❌ Erreur mentions légales:', err);
      setError(err.message || t('onboarding.legal.saveError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('onboarding.legal.title')}
        </h1>
        <p className="text-white/60">
          {t('onboarding.legal.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main card (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-white">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Conditions de paiement */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  {t('onboarding.legal.paymentTerms')}
                </label>
                <input
                  type="number"
                  value={formData.defaultPaymentTermsDays}
                  onChange={(e) => handleChange('defaultPaymentTermsDays', parseInt(e.target.value) || 30)}
                  min={0}
                  max={90}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300"
                />
                <p className="mt-2 text-sm text-white/50">
                  {t('onboarding.legal.paymentTermsHint')}
                </p>
              </div>

              {/* Suggestions */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-lime-500/20 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-lime-400" />
                  </div>
                  {t('onboarding.legal.suggestionsLabel')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {LEGAL_SUGGESTION_KEYS.map((suggestion, index) => {
                    const suggestionTitle = t(suggestion.titleKey);
                    const suggestionText = t(suggestion.textKey);
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleChange('legalNotesDefault', suggestionText)}
                        className="p-4 text-left bg-white/5 border border-white/10 rounded-2xl hover:border-lime-400/50 hover:bg-white/10 transition-all duration-300 group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="font-medium text-white text-sm mb-1 group-hover:text-lime-400 transition-colors">
                              {suggestionTitle}
                            </p>
                            <p className="text-xs text-white/50 line-clamp-2">
                              {suggestionText.substring(0, 60)}...
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-lime-400 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Textarea mentions */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  {t('onboarding.legal.customMentions')}
                </label>
                <textarea
                  value={formData.legalNotesDefault}
                  onChange={(e) => handleChange('legalNotesDefault', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300 resize-none"
                  placeholder={t('onboarding.legal.customMentionsPlaceholder')}
                />
                <p className="mt-2 text-sm text-white/50">
                  {t('onboarding.legal.mentionsAppearOn')}
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <ScrollText size={16} className="text-lime-400" />
              </div>
              <h3 className="text-base font-semibold text-white">
                {t('onboarding.legal.requiredMentions')}
              </h3>
            </div>
            <p className="text-sm text-white/60">
              {t('onboarding.legal.requiredMentionsInfo')}
            </p>
          </div>

          <div className="text-sm text-white/40 space-y-2">
            <p>{t('onboarding.legal.optional')}</p>
          </div>

          <div className="space-y-3 pt-4">
            <GlassButton
              onClick={onSkip}
              disabled={isLoading}
              variant="ghost"
              className="w-full"
            >
              <SkipForward className="w-5 h-5" />
              {t('onboarding.legal.skip')}
            </GlassButton>

            <GlassButton
              type="submit"
              onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
              disabled={isLoading}
              isLoading={isLoading}
              className="w-full"
            >
              {t('onboarding.legal.continue')}
              {!isLoading && <ArrowRight className="w-5 h-5" />}
            </GlassButton>
          </div>
        </div>
      </div>
    </div>
  );
};
